'use client';

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ArrowLeft, Plus, Trash, ExternalLink, Calendar, Pencil, Lock, Eye } from 'lucide-react';
import Link from 'next/link';
import { Stock } from '@/types';
import { StockChart } from './StockChart';
import { Button } from './Button';
import { NewsSection } from './NewsSection';
import { EarningsAnalysis } from './EarningsAnalysis';
import { EtfAllocations } from './EtfAllocations';
import { ResearchTab } from './ResearchTab';
import { useSubscription } from './SubscriptionContext';
import { formatNumber } from '@/lib/formatNumber';
import { findPriceForDate, fetchHistoricalPrice } from '@/lib/stockService';
import { getApiBase } from '@/lib/apiBase';

const formatMarketCap = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return 'N/A';
  if (value >= 1e12) return `$${(value / 1e12).toFixed(2)}T`;
  if (value >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
  if (value >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
  return `$${value.toLocaleString()}`;
};

const formatEarningsDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return 'N/A';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

function getAssetTypeLabel(stock: Stock): string {
  if (stock.instrumentType === 'ETF') {
    const name = (stock.name || '').toLowerCase();
    const bondKeywords = ['bond', 'fixed income', 'treasury', 'debt', 'aggregate'];
    if (bondKeywords.some(kw => name.includes(kw))) return 'Bond ETF';
    return 'Equity ETF';
  }
  return 'Equity';
}

interface StockDetailProps {
  stock: Stock;
  currency: 'USD' | 'CAD';
  exchangeRate: number;
  convert: (val: number | null | undefined) => number;
  onBack: () => void;
  onAddLot: (stockId: string, shares: number, price: number, date: string, currency: 'USD' | 'CAD', purchasedDate?: string) => void;
  onEditLot: (stockId: string, lotId: string, shares: number, price: number, currency: 'USD' | 'CAD') => void;
  onDeleteLot: (stockId: string, lotId: string) => void;
}

export const StockDetail: React.FC<StockDetailProps> = ({ stock, currency, exchangeRate, convert, onBack, onAddLot, onEditLot, onDeleteLot }) => {
  // Scroll to top when stock detail opens so the chart is visible first
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  const [isAddingLot, setIsAddingLot] = useState(false);
  const [newShares, setNewShares] = useState('');
  const [newPrice, setNewPrice] = useState('');
  const [newCurrency, setNewCurrency] = useState<'USD' | 'CAD'>('USD');
  const [newPurchasedDate, setNewPurchasedDate] = useState('');

  // Edit lot state
  const [editingLotId, setEditingLotId] = useState<string | null>(null);
  const [editShares, setEditShares] = useState('');
  const [editPrice, setEditPrice] = useState('');
  const [editCurrency, setEditCurrency] = useState<'USD' | 'CAD'>('USD');

  // Time range state
  type StockTimeRange = '1D' | '1W' | '1M' | '3M' | '6M' | 'YTD' | '1Y' | '2Y' | '5Y' | '10Y' | 'ALL';
  const [timeRange, setTimeRange] = useState<StockTimeRange>('1M');
  const [intradayData, setIntradayData] = useState<{ timestamp: string; price: number; open?: number; high?: number; low?: number; close?: number; volume?: number }[] | null>(null);
  const [isLoadingIntraday, setIsLoadingIntraday] = useState(false);

  // Tab state
  const [activeTab, setActiveTab] = useState<'overview' | 'allocations' | 'research'>('overview');
  const { tier } = useSubscription();
  const isPaid = tier === 'paid';

  const fetchIntradayData = useCallback(async () => {
    setIsLoadingIntraday(true);
    try {
      const res = await fetch(`${getApiBase()}/api/stocks/intraday?symbol=${encodeURIComponent(stock.symbol)}`);
      if (res.ok) {
        const data = await res.json();
        setIntradayData(data[stock.symbol] || []);
      }
    } catch (err) {
      console.error('Failed to fetch intraday data:', err);
    } finally {
      setIsLoadingIntraday(false);
    }
  }, [stock.symbol]);

  useEffect(() => {
    if (timeRange === '1D') {
      fetchIntradayData();
    }
  }, [timeRange, fetchIntradayData]);

  // Validation for new lot form
  const sharesValue = parseFloat(newShares);
  const priceValue = parseFloat(newPrice);
  const isSharesInvalid = newShares !== '' && (isNaN(sharesValue) || sharesValue <= 0);
  const isPriceInvalid = newPrice !== '' && (isNaN(priceValue) || priceValue <= 0);
  const hasValidationError = isSharesInvalid || isPriceInvalid;

  // Validation for edit lot form
  const editSharesValue = parseFloat(editShares);
  const editPriceValue = parseFloat(editPrice);
  const isEditSharesInvalid = editShares !== '' && (isNaN(editSharesValue) || editSharesValue <= 0);
  const isEditPriceInvalid = editPrice !== '' && (isNaN(editPriceValue) || editPriceValue <= 0);
  const hasEditValidationError = isEditSharesInvalid || isEditPriceInvalid;

  const totalShares = stock.lots.reduce((acc, lot) => acc + lot.shares, 0);

  // Calculate total cost in USD (normalizing CAD lots)
  const totalCostUSD = stock.lots.reduce((acc, lot) => {
    const lotCurrency = lot.currency || 'USD';
    const cost = lot.shares * lot.price;
    return acc + (lotCurrency === 'CAD' ? cost / exchangeRate : cost);
  }, 0);

  // Normalize Current Price to USD
  const priceInUSD = (stock.currency === 'CAD' && stock.currentPrice)
    ? stock.currentPrice / exchangeRate
    : (stock.currentPrice || 0);

  const avgCostUSD = totalShares > 0 ? totalCostUSD / totalShares : 0;
  const currentValueUSD = priceInUSD * totalShares;
  const totalReturnUSD = currentValueUSD - totalCostUSD;
  const returnPercent = totalCostUSD > 0 ? (totalReturnUSD / totalCostUSD) * 100 : 0;

  // Display Conversions
  const currencySymbol = currency === 'USD' ? '$' : 'C$';
  const displayCurrentPrice = convert(priceInUSD);
  const displayTotalCost = convert(totalCostUSD);
  const displayCurrentValue = convert(currentValueUSD);
  const displayTotalReturn = convert(totalReturnUSD);
  const displayAvgCost = convert(avgCostUSD);

  // Convert and filter history for chart based on selected time range
  const convertedHistory = useMemo(() => {
    const convertPrice = (p: number) =>
      convert(stock.currency === 'CAD' ? p / exchangeRate : p);

    if (timeRange === '1D') {
      // Use intraday data
      if (!intradayData) return [];
      return intradayData.map(d => ({
        date: d.timestamp,
        price: convertPrice(d.price),
        open: d.open != null ? convertPrice(d.open) : undefined,
        high: d.high != null ? convertPrice(d.high) : undefined,
        low: d.low != null ? convertPrice(d.low) : undefined,
        close: d.close != null ? convertPrice(d.close) : undefined,
        volume: d.volume,
      }));
    }

    const allHistory = stock.historicalData;
    if (allHistory.length === 0) return [];

    let startDate: Date;
    const now = new Date();

    if (timeRange === 'ALL') {
      startDate = new Date(0);
    } else {
      switch (timeRange) {
        case '1W':
          startDate = new Date(now);
          startDate.setDate(now.getDate() - 10); // 10 calendar days to capture ~5 trading days
          break;
        case '1M':
          startDate = new Date(now);
          startDate.setMonth(now.getMonth() - 1);
          break;
        case '3M':
          startDate = new Date(now);
          startDate.setMonth(now.getMonth() - 3);
          break;
        case '6M':
          startDate = new Date(now);
          startDate.setMonth(now.getMonth() - 6);
          break;
        case 'YTD':
          startDate = new Date(now.getFullYear(), 0, 1);
          break;
        case '1Y':
          startDate = new Date(now);
          startDate.setFullYear(now.getFullYear() - 1);
          break;
        case '2Y':
          startDate = new Date(now);
          startDate.setFullYear(now.getFullYear() - 2);
          break;
        case '5Y':
          startDate = new Date(now);
          startDate.setFullYear(now.getFullYear() - 5);
          break;
        case '10Y':
          startDate = new Date(now);
          startDate.setFullYear(now.getFullYear() - 10);
          break;
        default:
          startDate = new Date(now);
          startDate.setMonth(now.getMonth() - 1);
      }
    }

    startDate.setHours(0, 0, 0, 0);

    const filtered = allHistory.filter(d => {
      const dateObj = new Date(d.date + 'T00:00:00');
      return dateObj.getTime() >= startDate.getTime();
    });

    return filtered.map(d => ({
      ...d,
      price: convertPrice(d.price),
      open: d.open != null ? convertPrice(d.open) : undefined,
      high: d.high != null ? convertPrice(d.high) : undefined,
      low: d.low != null ? convertPrice(d.low) : undefined,
      close: d.close != null ? convertPrice(d.close) : undefined,
      volume: d.volume,
    }));
  }, [stock.historicalData, stock.currency, timeRange, intradayData, convert, exchangeRate]);

  // Lot price lines for chart (convert each lot's price to display currency)
  const lotPriceLines = stock.lots.map((lot, idx) => {
    const lotCurrency = lot.currency || 'USD';
    // Convert lot price to USD first, then to display currency
    const priceInUSD = lotCurrency === 'CAD' ? lot.price / exchangeRate : lot.price;
    return {
      price: convert(priceInUSD),
      label: `Lot ${idx + 1}`,
    };
  });

  const handlePurchasedDateChange = async (dateStr: string) => {
    setNewPurchasedDate(dateStr);
    if (dateStr) {
      // Try local history first, then fall back to API for dates outside 10-year range
      let historicalPrice = findPriceForDate(stock.historicalData, dateStr);
      if (historicalPrice === null) {
        historicalPrice = await fetchHistoricalPrice(stock.symbol, dateStr);
      }
      if (historicalPrice !== null) {
        setNewPrice(historicalPrice.toFixed(2));
      }
    }
  };

  const handleAddSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newShares || !newPrice || hasValidationError) return;

    const date = newPurchasedDate
      ? new Date(newPurchasedDate + 'T00:00:00').toISOString()
      : new Date().toISOString();
    onAddLot(stock.id, sharesValue, priceValue, date, newCurrency, newPurchasedDate || undefined);
    setIsAddingLot(false);
    setNewShares('');
    setNewPrice('');
    setNewCurrency('USD');
    setNewPurchasedDate('');
  };

  const handleStartEdit = (lot: { id: string; shares: number; price: number; currency: 'USD' | 'CAD' }) => {
    setEditingLotId(lot.id);
    setEditShares(lot.shares.toString());
    setEditPrice(lot.price.toString());
    setEditCurrency(lot.currency || 'USD');
  };

  const handleCancelEdit = () => {
    setEditingLotId(null);
    setEditShares('');
    setEditPrice('');
    setEditCurrency('USD');
  };

  const handleEditSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingLotId || !editShares || !editPrice || hasEditValidationError) return;

    onEditLot(stock.id, editingLotId, editSharesValue, editPriceValue, editCurrency);
    handleCancelEdit();
  };

  return (
    <div className="animate-in fade-in slide-in-from-right-4 duration-300">
      <div className="mb-6 flex items-center gap-4">
        <button
          onClick={onBack}
          className="rounded-xl p-2 transition-colors" style={{ backgroundColor: 'var(--color-surface-hover)', color: 'var(--color-text-secondary)' }}
        >
          <ArrowLeft size={20} />
        </button>
        <div>
          <h2 className="text-2xl font-bold text-[var(--color-text)] flex items-center gap-3">
             {stock.symbol} <span className="text-lg font-normal " style={{ color: 'var(--color-text-secondary)' }}>{stock.name}</span>
             {stock.exchange && (
                 <span className="text-xs font-medium px-2 py-1 rounded" style={{ backgroundColor: 'var(--color-surface-hover)', color: 'var(--color-text-primary)' }}>
                     {stock.exchange}
                 </span>
             )}
             {stock.instrumentType === 'ETF' && (
                 <span className="text-xs font-medium px-2 py-1 rounded" style={{ backgroundColor: 'color-mix(in srgb, var(--color-accent) 15%, transparent)', color: 'var(--color-accent-muted)', border: '1px solid color-mix(in srgb, var(--color-accent) 30%, transparent)' }}>
                     ETF
                 </span>
             )}
             {stock.watchlist && (
                 <span className="text-xs font-medium px-2 py-1 rounded flex items-center gap-1" style={{ backgroundColor: 'color-mix(in srgb, var(--color-accent) 15%, transparent)', color: 'var(--color-accent-muted)', border: '1px solid color-mix(in srgb, var(--color-accent) 30%, transparent)' }}>
                     <Eye size={12} /> Watchlist
                 </span>
             )}
          </h2>
        </div>
      </div>

      <div className="flex gap-1 rounded-full p-1 mb-6 w-fit" style={{ backgroundColor: 'var(--color-toggle-bg)' }}>
        {(stock.instrumentType === 'ETF'
          ? (['overview', 'allocations', 'research'] as const)
          : (['overview', 'research'] as const)
        ).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-3 py-1 text-sm font-medium rounded-full transition-all capitalize ${
              activeTab === tab
                ? 'shadow'
                : ''
            }`}
            style={activeTab === tab ? { backgroundColor: 'var(--color-toggle-active-accent)', color: '#fff' } : { color: 'var(--color-text-secondary)' }}
          >
            {tab}
          </button>
        ))}
      </div>

      {activeTab === 'research' ? (
        <ResearchTab symbol={stock.symbol} isPaid={isPaid} />
      ) : activeTab === 'allocations' && stock.instrumentType === 'ETF' ? (
        isPaid ? (
          <EtfAllocations symbol={stock.symbol} />
        ) : (
          <div className="rounded-2xl p-8 text-center" style={{ backgroundColor: 'var(--color-surface)' }}>
            <div className="mx-auto mb-4 w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: 'color-mix(in srgb, var(--color-accent) 20%, transparent)' }}>
              <Lock className="w-6 h-6" style={{ color: 'var(--color-accent-muted)' }} />
            </div>
            <h3 className="text-lg font-semibold text-[var(--color-text)] mb-2">ETF Allocations</h3>
            <p className="text-sm mb-4" style={{ color: 'var(--color-text-secondary)' }}>
              <Link href="/checkout" className="font-medium" style={{ color: 'var(--color-accent-muted)' }}>Upgrade to Pro</Link>{' '}
              to view sector and regional allocation breakdowns for ETFs.
            </p>
          </div>
        )
      ) : (
      <>
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main Chart Section */}
        <div className="rounded-2xl p-6 lg:col-span-2" style={{ backgroundColor: 'var(--color-surface)' }}>
           <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>Price History</h3>
              <span className="text-2xl font-bold text-[var(--color-text)]">
                {currencySymbol}{formatNumber(displayCurrentPrice) || '---'}
              </span>
           </div>
           <div className="flex flex-wrap gap-0.5 sm:gap-1 rounded-full p-0.5 sm:p-1 mb-4" style={{ backgroundColor: 'var(--color-toggle-bg)' }}>
             {(['1D', '1W', '1M', '3M', '6M', 'YTD', '1Y', '2Y', '5Y', '10Y', 'ALL'] as const).map((range) => (
               <button
                 key={range}
                 onClick={() => setTimeRange(range)}
                 className={`px-2 sm:px-3 py-0.5 sm:py-1 text-[10px] sm:text-xs font-medium rounded-full transition-all ${
                   timeRange === range
                     ? 'shadow'
                     : ''
                 }`}
                 style={timeRange === range ? { backgroundColor: 'var(--color-toggle-active-accent)', color: '#fff' } : { color: 'var(--color-text-secondary)' }}
               >
                 {range}
               </button>
             ))}
           </div>
           {timeRange === '1D' && isLoadingIntraday ? (
             <div className="flex h-64 items-center justify-center">
               <div className="flex items-center gap-3">
                 <div className="h-5 w-5 animate-spin rounded-full border-2" style={{ borderColor: 'var(--color-border)', borderTopColor: 'var(--color-accent-subtle)' }} />
                 <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Loading intraday data...</p>
               </div>
             </div>
           ) : (
             <StockChart
               data={convertedHistory}
               color={convertedHistory.length >= 2 && convertedHistory[convertedHistory.length - 1].price >= convertedHistory[0].price ? '#22c55e' : '#ef4444'}
               lotPriceLines={timeRange === '1D' ? [] : lotPriceLines}
               avgPriceLine={timeRange === '1D' ? undefined : (stock.lots.length >= 2 ? displayAvgCost : undefined)}
               isIntraday={timeRange === '1D'}
               timeRange={timeRange}
             />
           )}

           {stock.groundingUrls && stock.groundingUrls.length > 0 && (
            <div className="mt-4 pt-4" style={{ borderTop: '1px solid var(--color-divide)' }}>
              <p className="text-xs mb-2" style={{ color: 'var(--color-text-muted)' }}>Sources:</p>
              <div className="flex flex-wrap gap-2">
                {stock.groundingUrls.map((url, idx) => (
                  <a
                    key={idx}
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-1 text-xs text-[var(--color-accent-muted)] bg-[color-mix(in_srgb,var(--color-accent)_15%,transparent)] px-2 py-1 rounded"
                  >
                    <ExternalLink size={10} /> {new URL(url).hostname}
                  </a>
                ))}
              </div>
            </div>
           )}

           {/* AI Earnings Analysis - desktop only (inside Price History card) */}
           {stock.instrumentType !== 'ETF' && (
             <div className="hidden lg:block mt-6 pt-6" style={{ borderTop: '1px solid var(--color-divide)' }}>
               <EarningsAnalysis symbol={stock.symbol} isPaid={isPaid} embedded />
             </div>
           )}
        </div>

        {/* Stats & Breakdown */}
        <div className="space-y-6">
          {stock.watchlist && (
            <div className="rounded-2xl p-6" style={{ backgroundColor: 'var(--color-surface)' }}>
              {isAddingLot ? (
                <>
                  <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--color-text-primary)' }}>Add to Portfolio</h3>
                  <form onSubmit={handleAddSubmit} className="bg-[var(--color-surface-secondary)] p-3 rounded-xl">
                    <div className="grid grid-cols-12 gap-2 mb-2">
                      <div className="col-span-5">
                        <label className="block text-[10px] text-[var(--color-text-muted)] uppercase tracking-wider mb-1">Shares</label>
                        <input
                          type="number" step="any" required
                          value={newShares} onChange={e => setNewShares(e.target.value)}
                          className={`w-full bg-[var(--color-surface-hover)] border border-[var(--color-input)] rounded px-2 py-1 text-sm text-[var(--color-text)] outline-none ${isSharesInvalid ? 'border-red-500' : 'border-[var(--color-input)]'}`}
                        />
                      </div>
                      <div className="col-span-4">
                        <label className="block text-[10px] text-[var(--color-text-muted)] uppercase tracking-wider mb-1">Price</label>
                        <input
                          type="number" step="any" required
                          value={newPrice} onChange={e => setNewPrice(e.target.value)}
                          className={`w-full bg-[var(--color-surface-hover)] border border-[var(--color-input)] rounded px-2 py-1 text-sm text-[var(--color-text)] outline-none ${isPriceInvalid ? 'border-red-500' : 'border-[var(--color-input)]'}`}
                        />
                      </div>
                      <div className="col-span-3">
                        <label className="block text-[10px] text-[var(--color-text-muted)] uppercase tracking-wider mb-1">Currency</label>
                        <select
                          value={newCurrency}
                          onChange={(e) => setNewCurrency(e.target.value as 'USD' | 'CAD')}
                          className="w-full bg-[var(--color-surface-hover)] border border-[var(--color-input)] rounded px-2 py-1 text-sm text-[var(--color-text)] outline-none"
                        >
                          <option value="USD">USD</option>
                          <option value="CAD">CAD</option>
                        </select>
                      </div>
                    </div>

                    <div className="mb-2">
                      <label className="block text-[10px] text-[var(--color-text-muted)] uppercase tracking-wider mb-1">Purchase Date <span style={{ opacity: 0.5, textTransform: 'none' }}>(optional — auto-fills price)</span></label>
                      <input
                        type="date"
                        max={new Date().toISOString().split('T')[0]}
                        value={newPurchasedDate}
                        onChange={(e) => handlePurchasedDateChange(e.target.value)}
                        className="block w-full min-w-0 max-w-full box-border appearance-none bg-[var(--color-surface-hover)] border border-[var(--color-input)] rounded px-2 py-1 text-sm text-[var(--color-text)] outline-none"
                      />
                    </div>

                    {hasValidationError && (
                      <p className="text-red-400 text-xs mt-2">Shares and price must be positive numbers</p>
                    )}

                    <div className="flex gap-2 mt-4">
                      <Button type="submit" size="sm" className="w-full" disabled={hasValidationError}>Save</Button>
                      <Button type="button" variant="secondary" size="sm" onClick={() => setIsAddingLot(false)}>Cancel</Button>
                    </div>
                  </form>
                </>
              ) : (
                <div className="text-center">
                  <div className="mx-auto mb-3 w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: 'color-mix(in srgb, var(--color-accent) 20%, transparent)' }}>
                    <Eye className="w-6 h-6" style={{ color: 'var(--color-accent-muted)' }} />
                  </div>
                  <h3 className="text-lg font-semibold text-[var(--color-text)] mb-2">Watchlist Stock</h3>
                  <p className="text-sm mb-4" style={{ color: 'var(--color-text-secondary)' }}>
                    Add a lot to move this stock into your portfolio and start tracking performance.
                  </p>
                  <Button size="sm" onClick={() => setIsAddingLot(true)}>
                    <Plus size={14} className="mr-1" /> Add to Portfolio
                  </Button>
                </div>
              )}
            </div>
          )}

          {!stock.watchlist && (
          <div className="rounded-2xl p-6" style={{ backgroundColor: 'var(--color-surface)' }}>
            <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--color-text-primary)' }}>Performance ({currency})</h3>
            <div className="space-y-4">
              <div className="flex justify-between">
                <span style={{ color: 'var(--color-text-secondary)' }}>Market Value</span>
                <span className="text-[var(--color-text)] font-medium">{currencySymbol}{formatNumber(displayCurrentValue)}</span>
              </div>
              <div className="flex justify-between">
                <span style={{ color: 'var(--color-text-secondary)' }}>Total Cost</span>
                <span className="font-medium" style={{ color: 'var(--color-text-primary)' }}>{currencySymbol}{formatNumber(displayTotalCost)}</span>
              </div>
              <div className="flex justify-between">
                <span style={{ color: 'var(--color-text-secondary)' }}>Total Return</span>
                <span className={`font-bold ${totalReturnUSD >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {totalReturnUSD >= 0 ? '+' : ''}{formatNumber(displayTotalReturn)} ({formatNumber(returnPercent)}%)
                </span>
              </div>
              <div className="h-px my-2" style={{ backgroundColor: 'var(--color-divide)' }}></div>
              <div className="flex justify-between">
                 <span style={{ color: 'var(--color-text-secondary)' }}>Avg Cost / Share</span>
                 <span style={{ color: 'var(--color-text-primary)' }}>{currencySymbol}{formatNumber(displayAvgCost)}</span>
              </div>
              <div className="flex justify-between">
                 <span style={{ color: 'var(--color-text-secondary)' }}>Shares Owned</span>
                 <span className="text-[var(--color-text)]">{formatNumber(totalShares)}</span>
              </div>
              <div className="h-px my-2" style={{ backgroundColor: 'var(--color-divide)' }}></div>
              {stock.instrumentType !== 'ETF' && (
              <div className="flex justify-between">
                 <span style={{ color: 'var(--color-text-secondary)' }}>Market Cap</span>
                 <span style={{ color: 'var(--color-text-primary)' }}>{formatMarketCap(stock.marketCap)}</span>
              </div>
              )}
              {stock.instrumentType !== 'ETF' && (
              <div className="flex justify-between items-center">
                 <span className="flex items-center gap-1" style={{ color: 'var(--color-text-secondary)' }}>
                   <Calendar size={12} /> Next Earnings
                 </span>
                 <span style={{ color: 'var(--color-text-primary)' }}>{formatEarningsDate(stock.nextEarningsDate)}</span>
              </div>
              )}
            </div>
          </div>
          )}

          <div className="rounded-2xl p-6" style={{ backgroundColor: 'var(--color-surface)' }}>
            <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--color-text-primary)' }}>Information</h3>
            <div className="space-y-4">
              <div className="flex justify-between">
                <span style={{ color: 'var(--color-text-secondary)' }}>Type</span>
                <span style={{ color: 'var(--color-text-primary)' }}>{getAssetTypeLabel(stock)}</span>
              </div>
              {stock.instrumentType !== 'ETF' && stock.sector && (
                <div className="flex justify-between">
                  <span style={{ color: 'var(--color-text-secondary)' }}>Sector</span>
                  <span style={{ color: 'var(--color-text-primary)' }}>{stock.sector}</span>
                </div>
              )}
            </div>
          </div>

          {!stock.watchlist && (
          <div className="rounded-2xl p-6" style={{ backgroundColor: 'var(--color-surface)' }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>Lots</h3>
              <button
                onClick={() => setIsAddingLot(!isAddingLot)}
                className="text-sm flex items-center gap-1" style={{ color: 'var(--color-accent-muted)' }}
              >
                <Plus size={16} /> Add Lot
              </button>
            </div>

            {isAddingLot && (
              <form onSubmit={handleAddSubmit} className="mb-4 bg-[var(--color-surface-secondary)] p-3 rounded-xl">
                 <div className="grid grid-cols-12 gap-2 mb-2">
                   <div className="col-span-5">
                     <label className="block text-[10px] text-[var(--color-text-muted)] uppercase tracking-wider mb-1">Shares</label>
                     <input
                        type="number" step="any" required
                        value={newShares} onChange={e => setNewShares(e.target.value)}
                        className={`w-full bg-[var(--color-surface-hover)] border border-[var(--color-input)] rounded px-2 py-1 text-sm text-[var(--color-text)] outline-none ${isSharesInvalid ? 'border-red-500' : 'border-[var(--color-input)]'}`}
                     />
                   </div>
                   <div className="col-span-4">
                     <label className="block text-[10px] text-[var(--color-text-muted)] uppercase tracking-wider mb-1">Price</label>
                     <input
                        type="number" step="any" required
                        value={newPrice} onChange={e => setNewPrice(e.target.value)}
                        className={`w-full bg-[var(--color-surface-hover)] border border-[var(--color-input)] rounded px-2 py-1 text-sm text-[var(--color-text)] outline-none ${isPriceInvalid ? 'border-red-500' : 'border-[var(--color-input)]'}`}
                     />
                   </div>
                   <div className="col-span-3">
                     <label className="block text-[10px] text-[var(--color-text-muted)] uppercase tracking-wider mb-1">Currency</label>
                     <select
                        value={newCurrency}
                        onChange={(e) => setNewCurrency(e.target.value as 'USD' | 'CAD')}
                        className="w-full bg-[var(--color-surface-hover)] border border-[var(--color-input)] rounded px-2 py-1 text-sm text-[var(--color-text)] outline-none"
                     >
                        <option value="USD">USD</option>
                        <option value="CAD">CAD</option>
                     </select>
                   </div>
                 </div>

                 <div className="mb-2">
                   <label className="block text-[10px] text-[var(--color-text-muted)] uppercase tracking-wider mb-1">Purchase Date <span style={{ opacity: 0.5, textTransform: 'none' }}>(optional — auto-fills price)</span></label>
                   <input
                     type="date"
                     max={new Date().toISOString().split('T')[0]}
                     value={newPurchasedDate}
                     onChange={(e) => handlePurchasedDateChange(e.target.value)}
                     className="block w-full min-w-0 max-w-full box-border appearance-none bg-[var(--color-surface-hover)] border border-[var(--color-input)] rounded px-2 py-1 text-sm text-[var(--color-text)] outline-none"
                   />
                 </div>

                 {hasValidationError && (
                   <p className="text-red-400 text-xs mt-2">Shares and price must be positive numbers</p>
                 )}

                 <div className="flex gap-2 mt-4">
                   <Button type="submit" size="sm" className="w-full" disabled={hasValidationError}>Save</Button>
                   <Button type="button" variant="secondary" size="sm" onClick={() => setIsAddingLot(false)}>Cancel</Button>
                 </div>
              </form>
            )}

            <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
              {stock.lots.length === 0 ? (
                <p className="text-center text-sm py-4" style={{ color: 'var(--color-text-muted)' }}>No lots added yet.</p>
              ) : (
                stock.lots.map((lot, lotIndex) => {
                  const lotCurrency = lot.currency || 'USD';
                  const lotSymbol = lotCurrency === 'USD' ? '$' : 'C$';
                  const lotLabel = `Lot ${lotIndex + 1}`;

                  // Calculate USD values for internal comparison
                  const costInUSD = lotCurrency === 'CAD' ? (lot.shares * lot.price) / exchangeRate : (lot.shares * lot.price);
                  const valueInUSD = priceInUSD * lot.shares;
                  const lotReturnUSD = valueInUSD - costInUSD;
                  const isLotProfit = lotReturnUSD >= 0;

                  // Show edit form for this lot
                  if (editingLotId === lot.id) {
                    return (
                      <form key={lot.id} onSubmit={handleEditSubmit} className="p-3 bg-[var(--color-surface-secondary)] rounded-xl border border-[var(--color-accent-subtle)]">
                        <div className="text-xs text-[var(--color-text-secondary)] mb-2 font-medium">{lotLabel}</div>
                        <div className="grid grid-cols-12 gap-2 mb-2">
                          <div className="col-span-5">
                            <label className="block text-[10px] text-[var(--color-text-muted)] uppercase tracking-wider mb-1">Shares</label>
                            <input
                              type="number" step="any" required
                              value={editShares} onChange={e => setEditShares(e.target.value)}
                              className={`w-full bg-[var(--color-surface-hover)] border border-[var(--color-input)] rounded px-2 py-1 text-sm text-[var(--color-text)] outline-none ${isEditSharesInvalid ? 'border-red-500' : 'border-[var(--color-input)]'}`}
                            />
                          </div>
                          <div className="col-span-4">
                            <label className="block text-[10px] text-[var(--color-text-muted)] uppercase tracking-wider mb-1">Price</label>
                            <input
                              type="number" step="any" required
                              value={editPrice} onChange={e => setEditPrice(e.target.value)}
                              className={`w-full bg-[var(--color-surface-hover)] border border-[var(--color-input)] rounded px-2 py-1 text-sm text-[var(--color-text)] outline-none ${isEditPriceInvalid ? 'border-red-500' : 'border-[var(--color-input)]'}`}
                            />
                          </div>
                          <div className="col-span-3">
                            <label className="block text-[10px] text-[var(--color-text-muted)] uppercase tracking-wider mb-1">Currency</label>
                            <select
                              value={editCurrency}
                              onChange={(e) => setEditCurrency(e.target.value as 'USD' | 'CAD')}
                              className="w-full bg-[var(--color-surface-hover)] border border-[var(--color-input)] rounded px-2 py-1 text-sm text-[var(--color-text)] outline-none"
                            >
                              <option value="USD">USD</option>
                              <option value="CAD">CAD</option>
                            </select>
                          </div>
                        </div>

                        {hasEditValidationError && (
                          <p className="text-red-400 text-xs mt-2">Shares and price must be positive numbers</p>
                        )}

                        <div className="flex gap-2 mt-3">
                          <Button type="submit" size="sm" className="flex-1" disabled={hasEditValidationError}>Save</Button>
                          <Button type="button" variant="secondary" size="sm" onClick={handleCancelEdit}>Cancel</Button>
                        </div>
                      </form>
                    );
                  }

                  return (
                    <div key={lot.id} className="flex items-center justify-between p-3 bg-[var(--color-surface-secondary)] rounded-xl group">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-[var(--color-text-secondary)] bg-[var(--color-surface-secondary)] px-1.5 py-0.5 rounded">{lotLabel}</span>
                          <span className="text-sm font-medium text-[var(--color-text-primary)]">
                            {formatNumber(lot.shares)} shares @ {lotSymbol}{formatNumber(lot.price)}
                          </span>
                        </div>
                        <div className="text-xs text-[var(--color-text-muted)] mt-1">
                          {lot.purchasedDate
                            ? `Purchased ${new Date(lot.purchasedDate + 'T00:00:00').toLocaleDateString()}`
                            : new Date(lot.date).toLocaleDateString()}
                        </div>
                      </div>
                      <div className="text-right flex items-center gap-3">
                        <div>
                          <div className={`text-sm font-medium ${isLotProfit ? 'text-green-400' : 'text-red-400'}`}>
                             {isLotProfit ? '+' : ''}{currencySymbol}{formatNumber(convert(lotReturnUSD))}
                          </div>
                          <div className="text-xs text-[var(--color-text-muted)]">
                            {formatNumber((lotReturnUSD / costInUSD) * 100, 1)}%
                          </div>
                        </div>
                        <button
                          onClick={() => handleStartEdit(lot)}
                          className="opacity-0 group-hover:opacity-100 text-[var(--color-text-muted)] hover:text-[var(--color-accent-muted)] transition-opacity"
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          onClick={() => onDeleteLot(stock.id, lot.id)}
                          className="opacity-0 group-hover:opacity-100 text-[var(--color-text-muted)] hover:text-red-400 transition-opacity"
                        >
                          <Trash size={14} />
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
          )}
        </div>
      </div>

      {/* AI Earnings Analysis - mobile only (standalone card) */}
      {stock.instrumentType !== 'ETF' && (
      <div className="mt-6 lg:hidden">
        <EarningsAnalysis symbol={stock.symbol} isPaid={isPaid} />
      </div>
      )}

      {/* News Section */}
      <div className="mt-6">
        <NewsSection symbol={stock.symbol} />
      </div>
      </>
      )}
    </div>
  );
};
